import Vue from "vue";
import Vuex from "vuex";
import API from "../api/api";

Vue.use(Vuex);

// 從 localStorage 恢復 userInfo
const savedUserInfo = window.localStorage.getItem("userInfo");
let parsedUserInfo = {};
if (savedUserInfo) {
  try {
    parsedUserInfo = JSON.parse(savedUserInfo);
  } catch (error) {
    console.error("Error parsing userInfo from localStorage:", error);
    console.warn("Invalid JSON in localStorage, clearing userInfo");
    window.localStorage.removeItem("userInfo");
    parsedUserInfo = {};
  }
}

function getFallbackUserInfo() {
  try {
    const userInfoStr = window.localStorage.getItem("userInfo");
    return userInfoStr ? JSON.parse(userInfoStr) : {};
  } catch (error) {
    console.error("Error parsing fallback userInfo from localStorage:", error);
    return {};
  }
}

function getFallbackUserRole() {
  const userRole = window.localStorage.getItem("userRole");
  if (!userRole) {
    return "";
  }

  try {
    const parsedRole = JSON.parse(userRole);
    if (Array.isArray(parsedRole)) {
      return parsedRole[0] || "";
    }
    return parsedRole || "";
  } catch (error) {
    return userRole;
  }
}

const BRANCH_AREA_LIST = [
  { area: "全部", cities: ["全部"] },
  { area: "北部", cities: ["臺北市", "新北市", "桃園市", "新竹市", "新竹縣", "宜蘭縣", "基隆市"] },
  { area: "中部", cities: ["臺中市", "彰化縣", "苗栗縣", "南投縣", "雲林縣"] },
  { area: "南部", cities: ["高雄市", "臺南市", "嘉義市", "嘉義縣", "屏東縣"] },
  { area: "東部", cities: ["花蓮縣", "臺東縣", "澎湖縣", "金門縣", "連江縣"] },
];

function parseBranchLocationFromAddress(fullAddress) {
  if (!fullAddress) {
    return { branchArea: "", branchCity: "", branchDist: "" };
  }

  let branchCity = "";
  let branchDist = "";
  const regex = /^(.*?(?:市|縣))(.{1,6}?(?:區|市|鎮|鄉))/;
  const m = fullAddress.match(regex);

  if (m) {
    branchCity = (m[1] || "").replace(/台/g, "臺");
    branchDist = m[2] || "";
  } else {
    const allCities = BRANCH_AREA_LIST.reduce((acc, it) => acc.concat(it.cities), []);
    let matchedCity = "";
    for (const cityName of allCities) {
      if (fullAddress.startsWith(cityName) && cityName.length > matchedCity.length) {
        matchedCity = cityName;
      }
    }
    branchCity = (matchedCity || fullAddress.substr(0, 3)).replace(/台/g, "臺");
    const remainingAddr = fullAddress.slice(branchCity.length);
    const suffixes = ["區", "市", "鎮", "鄉"];
    let firstIdx = -1;
    for (const suffix of suffixes) {
      const idx = remainingAddr.indexOf(suffix);
      if (idx !== -1 && (firstIdx === -1 || idx < firstIdx)) {
        firstIdx = idx;
      }
    }
    branchDist = firstIdx !== -1 ? remainingAddr.substring(0, firstIdx + 1) : "";
  }

  let branchArea = "";
  for (const areaItem of BRANCH_AREA_LIST) {
    if (areaItem.cities.includes(branchCity)) {
      branchArea = areaItem.area;
      break;
    }
  }

  return { branchArea, branchCity, branchDist };
}

export default new Vuex.Store({
  state: {
    loading: false,
    token: "",
    userInfo: parsedUserInfo,
    branchArea: "",
    branchCity: "",
    branchDist: "",
    menuList: null,
    routerFrom: "",
    un: parsedUserInfo.UserName || "",
  },
  mutations: {
    SETLOADING(state, payload) {
      state.loading = payload;
    },
    SET_TOKEN(state, payload) {
      state.token = payload;
    },
    SET_USERINFO(state, payload) {
      state.userInfo = payload;
    },
    SET_BRANCH_LOCATION(state, payload) {
      state.branchArea = payload.branchArea || "";
      state.branchCity = payload.branchCity || "";
      state.branchDist = payload.branchDist || "";
    },
    SET_MENU(state, payload) {
      // console.log("Payload",payload);
      state.menuList = payload;
    },
    SET_ROUTERFROM(state, payload) {
      state.routerFrom = payload;
    },
    SET_un(state, payload) {
      state.un = payload;
    },
  },
  actions: {
    loadingHandler(state, payload) {
      state.commit("SETLOADING", payload);
    },
    RefreshSessionToken({ state, commit, dispatch }, payload = {}) {
      const logoutOnFail = !!payload.logoutOnFail;
      let userInfo = state.userInfo || {};

      // 如果 state 為空（新分頁或 F5），嘗試從 localStorage 讀取
      if (!userInfo.UserID || !userInfo.refresh_token) {
        try {
          const stored = window.localStorage.getItem("userInfo");
          userInfo = stored ? JSON.parse(stored) : {};
        } catch (e) {
          console.error("Error parsing localStorage userInfo:", e);
        }
      }

      if (!userInfo.UserID || !userInfo.refresh_token) {
        if (logoutOnFail && userInfo.UserID) {
          dispatch("Logout");
        }
        return Promise.resolve(false);
      }

      const qry = {
        UserID: userInfo.UserID,
        AccessToken: userInfo.access_token || state.token || "",
        RefreshToken: userInfo.refresh_token,
        isRefresh: true,
      };

      return API.apimgrs
        .refresh_token(qry)
        .then((res) => {
          if (!res || !res.access_token) {
            if (logoutOnFail) {
              dispatch("Logout");
            }
            return false;
          }

          const refreshedUserInfo = {
            ...userInfo,
            access_token: res.access_token,
            refresh_token: res.refresh_token || userInfo.refresh_token,
          };

          commit("SET_TOKEN", res.access_token);
          commit("SET_USERINFO", refreshedUserInfo);
          window.localStorage.setItem("userInfo", JSON.stringify(refreshedUserInfo));
          return true;
        })
        .catch((err) => {
          console.log("RefreshSessionToken Error", err);
          if (logoutOnFail) {
            dispatch("Logout");
          }
          return false;
        });
    },
    InitBranchLocation({ state, commit }, payload = {}) {
      const force = !!payload.force;
      if (!force && state.branchArea && state.branchCity) {
        return Promise.resolve(state.branchCity);
      }

      const userInfo = state.userInfo || {};
      const deptID = userInfo.DeptID;
      if (!deptID) {
        return Promise.resolve("");
      }

      const roleRaw = Array.isArray(userInfo.Role) ? userInfo.Role[0] || "" : userInfo.RoleName || userInfo.Role || "";
      const roleName = String(roleRaw).toLowerCase();
      const qry = {
        Page_Size: 1,
        Page_Num: 1,
        DeptID: deptID,
        isSys: true,
      };

      if (roleName === "hd" || roleName === "hc" || roleName === "hc1") {
        qry.DeptID = "999";
      }

      return API.apidivison
        .division_info_list(qry)
        .then((res) => {
          if (!(res && res.data && res.data[0] && res.data[0].Address)) {
            return "";
          }

          const fullAddress = res.data[0].Address || "";
          const parsed = parseBranchLocationFromAddress(fullAddress);
          commit("SET_BRANCH_LOCATION", parsed);
          window.localStorage.removeItem("branchArea");
          window.localStorage.removeItem("branchCity");
          window.localStorage.removeItem("branchDist");
          return parsed.branchCity;
        })
        .catch((err) => {
          console.log("InitBranchLocation Error", err);
          return "";
        });
    },
    Login({ commit }, userInfo) {
      console.log(userInfo);
      return new Promise((resolve, reject) => {
        //產生formData 物件...
        var formData = new FormData();
        formData.append("username", userInfo.username);
        formData.append("password", userInfo.password);
        const start = Date.now();
        API.apimgrs
          .login(formData)
          .then((res) => {
            console.log(res);
            const millis = Date.now() - start;
            console.log(`呼叫登入API ... = ${Math.floor(millis / 1000)}`);
            if (!!res) {
              //console.log("res success SETTOKEN",res);
              commit("SET_un", res.UserName);

              commit("SET_TOKEN", res.access_token);
              window.localStorage.setItem("userID", res.UserID);
              window.localStorage.setItem("deptID", res.DeptID);
              window.localStorage.setItem("comID", res.ComID);
              window.localStorage.setItem("userRole", res.Role);
              // window.localStorage.setItem("uName", un);
              window.localStorage.setItem("userInfo", JSON.stringify(res));
              commit("SET_USERINFO", res);

              // console.log("Menu",res.Menu);
              const strMenuFlatter = JSON.stringify(res.Menu);
              window.localStorage.setItem("menuFlatter", strMenuFlatter);
              let mainMenu = res.Menu.filter((main) => main.Pid === 0);
              console.log("mainMenu", mainMenu);
              const childrenMenu = res.Menu.filter((children) => children.Pid !== 0);
              // console.log("childrenMenu",childrenMenu);
              const menuList = mainMenu.map((p) => {
                p.children = childrenMenu.filter((c) => c.Pid == p.ID);
                return p;
              });

              console.log(menuList);
              const strMenuList = JSON.stringify(menuList);
              // console.log(strMenuList);
              window.localStorage.setItem("menuList", strMenuList);
              commit("SET_MENU", menuList);

              resolve("success");
            } else {
              resolve(res);
            }
          })
          .catch((err) => {
            //console.log(err.response);
            reject(err.response);
          });
      });
    },

    // 台企, 用部門別來登入...
    LoginDept({ commit }, userInfo) {
      console.log(userInfo);
      return new Promise((resolve, reject) => {
        const start = Date.now();
        API.apimgrs
          .createToken(userInfo)
          .then((res) => {
            console.log(res);
            const millis = Date.now() - start;
            //  console.log(`呼叫登入API ... = ${Math.floor(millis / 1000 )}`);
            if (res) {
              // 設定
              commit("SET_un", res.UserName);
              //console.log("encode_un",un);
              // console.log("un",getun(res));
              commit("SET_TOKEN", res.access_token);

              window.localStorage.setItem("userID", res.UserID);
              window.localStorage.setItem("deptID", res.DeptID);
              window.localStorage.setItem("comID", res.ComID);
              window.localStorage.setItem("userRole", res.Role);

              // window.localStorage.setItem("uName", un);
              window.localStorage.setItem("userInfo", JSON.stringify(res));

              // console.log("登入者", res);
              commit("SET_USERINFO", res);

              // console.log("Menu",res.Menu);
              const strMenuFlatter = JSON.stringify(res.Menu);
              window.localStorage.setItem("menuFlatter", strMenuFlatter);
              let mainMenu = res.Menu.filter((main) => main.Pid === 0);
              console.log("mainMenu", mainMenu);
              const childrenMenu = res.Menu.filter((children) => children.Pid !== 0);
              // console.log("childrenMenu",childrenMenu);
              const menuList = mainMenu.map((p) => {
                p.children = childrenMenu.filter((c) => c.Pid == p.ID);
                return p;
              });

              console.log(menuList);
              const strMenuList = JSON.stringify(menuList);
              // console.log(strMenuList);
              window.localStorage.setItem("menuList", strMenuList);
              commit("SET_MENU", menuList);
              resolve("success");
            } else {
              resolve(res);
            }
          })
          .catch((err) => {
            reject(err.response);
          });
      });
    },

    // 台企, 用ePortal SSO來登入...
    LoginePortal({ commit }, userInfo) {
      // console.log("呼叫Store.LoginePortal",userInfo);
      return new Promise((resolve, reject) => {
        console.log("呼叫Store.LoginePortal", userInfo);
        const start = Date.now();

        API.apimgrs
          .eportal_login(userInfo)
          .then((res) => {
            console.log("已登入後端權限", res);
            const millis = Date.now() - start;
            //  console.log(`呼叫登入API ... = ${Math.floor(millis / 1000 )}`);
            if (res) {
              commit("SET_un", res.UserName);

              commit("SET_TOKEN", res.access_token);

              window.localStorage.setItem("userID", res.UserID);
              window.localStorage.setItem("deptID", res.DeptID);
              window.localStorage.setItem("comID", res.ComID);
              window.localStorage.setItem("userRole", res.Role);

              // window.localStorage.setItem("uName", un);
              window.localStorage.setItem("userInfo", JSON.stringify(res));
              console.log("登入者", res);
              commit("SET_USERINFO", res);

              console.log("Menu", res.Menu);
              let mainMenu = res.Menu.filter((main) => main.Pid === 0);
              // console.log("mainMenu",mainMenu);
              const childrenMenu = res.Menu.filter((children) => children.Pid !== 0);
              // console.log("childrenMenu",childrenMenu);
              const menuList = mainMenu.map((p) => {
                p.children = childrenMenu.filter((c) => c.Pid == p.ID);
                return p;
              });

              // console.log(menuList);
              const strMenuList = JSON.stringify(menuList);
              // console.log(strMenuList);
              window.localStorage.setItem("menuList", strMenuList);

              const strMenuFlatter = JSON.stringify(res.Menu);
              window.localStorage.setItem("menuFlatter", strMenuFlatter);

              commit("SET_MENU", menuList);
              resolve("success");
            } else {
              resolve(res);
            }
          })
          .catch((err) => {
            reject(err.response);
          });
      });
    },

    Logout({ commit }) {
      // 清除 token/userInfo
      const userIdForCleanup = (this && this.state && this.state.userInfo && this.state.userInfo.UserID) || window.localStorage.getItem("userID") || "";
      commit("SET_TOKEN", "");
      commit("SET_USERINFO", {});
      // 也移除 cross-tab 的公告顯示紀錄（若存在）
      try {
        if (userIdForCleanup) {
          const announceKey = `announcement_shown_${userIdForCleanup}`;
          window.localStorage.removeItem(announceKey);
        }
      } catch (e) {
        // ignore
      }
      commit("SET_BRANCH_LOCATION", { branchArea: "", branchCity: "", branchDist: "" });
      window.localStorage.removeItem("userID");
      window.localStorage.removeItem("deptID");
      window.localStorage.removeItem("comID");
      window.localStorage.removeItem("userRole");
      window.localStorage.removeItem("userInfo");
      window.localStorage.removeItem("menuList");
      window.localStorage.removeItem("menuFlatter");
      window.localStorage.removeItem("branchArea");
      window.localStorage.removeItem("branchCity");
      window.localStorage.removeItem("branchDist");
    },
    GetInfo({ getters }) {
      // console.log("GetInfo打api");
      return new Promise((resolve, reject) => {
        const req = { UserID: getters.userID, Page_Size: "10", Page_Num: "1" };
        // console.log("post",req);
        resolve();
        API.apimgrs
          .getInfo(req)
          .then((res) => {
            //console.log("res.data[0]",res.data[0]);
            // commit("SET_USERINFO", res.data[0]);
            resolve();
          })
          .catch(() => {
            console.log("GetInfo Error!");
            reject();
          });
      });
    },

    ReloadMenu({ commit }) {
      // console.log(window.localStorage.getItem("menuList"));
      let mList = JSON.parse(window.localStorage.getItem("menuList"));
      if (mList) {
        // console.log("找出MenuList",mList);
        commit("SET_MENU", mList);
      }

      // reload時 , userInfo也不見了.. 所以要
      // this.$store.state.userInfo
    },
    GetMenu({ commit }) {
      API.apimgrs
        .getMenu()
        .then((res) => {
          const { menu } = res;
          // console.log("menu",menu);
          let mainMenu = menu.filter((main) => main.Pid === 0);
          //console.log("mainMenu",mainMenu);
          const childrenMenu = menu.filter((children) => children.Pid !== 0);
          const menuList = mainMenu.map((p) => {
            p.children = childrenMenu.filter((c) => c.Pid == p.ID);
            return p;
          });
          // console.log(menuList);
          commit("SET_MENU", menuList);
        })
        .catch((err) => {
          console.log("err GetMemu", err);
        });
    },
    SaveHistory({ commit }, routerFrom) {
      commit("SET_ROUTERFROM", routerFrom.name);
    },
  },

  getters: {
    userInfo: (state) => {
      if (state.userInfo && Object.keys(state.userInfo).length > 0) {
        return state.userInfo;
      }
      return getFallbackUserInfo();
    },
    un: (state) => state.un,
    branchArea: (state) => state.branchArea,
    branchCity: (state) => state.branchCity,
    branchDist: (state) => state.branchDist,
    userID: (state, getters) => getters.userInfo.UserID || window.localStorage.getItem("userID") || "",
    deptID: (state, getters) => getters.userInfo.DeptID || window.localStorage.getItem("deptID") || "",
    comID: (state, getters) => getters.userInfo.ComID || window.localStorage.getItem("comID") || "",
    userRole: (state, getters) => {
      const userInfo = getters.userInfo;
      if (Array.isArray(userInfo.Role) && userInfo.Role.length > 0) {
        return userInfo.Role[0] || "";
      }
      if (typeof userInfo.Role === "string" && userInfo.Role) {
        return userInfo.Role;
      }
      if (userInfo.RoleName) {
        return userInfo.RoleName;
      }
      return getFallbackUserRole();
    },
    userMenu: (state, getters) => getters.userInfo.Menu || [],
  },
});
